import React, { useCallback, useMemo, createElement, useState, useEffect, useRef } from "react";
import { useApolloClient, NormalizedCacheObject, ApolloClient } from "@apollo/client";
import { Grid, AgGrid, Flex, Button, Text } from "@jsq/ds/core";
import { useFeatureFlag } from "@jsq/flags-react";
import { ControlTowerContext } from "#utils/context";
import { SPACING } from "#utils/spacing";
import { useControlTowerContext } from "../ControlTowerContext";
import { WorkflowDetailsDrawer } from "../WorkflowDetailsDrawer";
import { WorkflowEngine } from "../WorkflowEngine";
import { WorkflowTabs } from "../WorkflowTabs";
import { getControlTowerTabs, WORKFLOW_TAB_KEYS } from "../WorkflowTabs/tabConfig";
import { getColumnDefs } from "./columnDefs";
import { BulkActionsPanel } from "./components/BulkActionsPanel";
import { ExportPanel } from "./components/ExportPanel";
import { SavedViewPanel } from "./components/SavedViewPanel";
import { gridOptions as defaultGridOptions } from "./gridOptions";
import { useGridViewManager } from "./hooks/useGridViewManager";
import { useWorkflowSlideoutDrawer } from "./hooks/useWorkflowSlideoutDrawer";
import {
  container,
  tableContainer,
  gridWrapper,
  tableHeader,
  tableHeaderText,
} from "./styles_vanilla.css";
import { TicketsGraphqlDataSource } from "./ticketsGraphqlDataSource";
import { TicketsTableProps, TicketRowItem, ExtendedTicketFilters } from "./types";

export type { TicketsTableProps };

export function openTicketsInJira(selectedRows: TicketRowItem[]): void {
  if (selectedRows.length === 0) return;

  const firstTicket = selectedRows[0];
  const firstTicketUrl = new URL(firstTicket.jiraLink);
  const jiraBaseUrl = `${firstTicketUrl.protocol}//${firstTicketUrl.host}`;
  const jiraProjectKey = firstTicket.key.split("-")[0];

  const ticketKeys = selectedRows.map((row) => row.key);
  const jqlQuery = `issue IN (${ticketKeys.join(", ")})`;
  const encodedJql = encodeURIComponent(jqlQuery);
  const jiraUrl = `${jiraBaseUrl}/jira/software/c/projects/${jiraProjectKey}/issues/?jql=${encodedJql}`;

  window.open(jiraUrl, "_blank");
}

export const TicketsTable: React.FC<TicketsTableProps> = ({
  initialFilters,
  onFilterChanged,
  onSavedViewFilterApplied,
  onSortsChange,
  onClearFilters,
  excludeAllCompleted,
  clearDashboardFilters,
  defaultActiveTabKey,
  dashboardFilterModel,
}) => {
  const apolloClient = useApolloClient() as ApolloClient<NormalizedCacheObject>;
  const context = useControlTowerContext();
  const isCanopy = context === ControlTowerContext.CANOPY;
  const isGPX = context === ControlTowerContext.GPX;
  const showGpControlTower = useFeatureFlag("showGpControlTower", false);
  const crltWorkflowEngine = useFeatureFlag("crltWorkflowEngine", false);
  const [activeTabKey, setActiveTabKey] = useState<string>(
    defaultActiveTabKey ?? WORKFLOW_TAB_KEYS.ASSIGNED_TO_ME,
  );
  const [workflowHasActiveFilters, setWorkflowHasActiveFilters] = useState(false);
  const [workflowClearTrigger, setWorkflowClearTrigger] = useState(0);
  const { selectedWorkflow, onClose, onCellClicked } = useWorkflowSlideoutDrawer();
  const [gridApi, setGridApi] = useState<AgGrid.GridApi>();
  const initialFiltersRef = useRef(initialFilters);
  const prevClearDashboardFiltersRef = useRef(clearDashboardFilters);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);

  useEffect(() => {
    if (
      crltWorkflowEngine &&
      dashboardFilterModel &&
      Object.keys(dashboardFilterModel).length > 0
    ) {
      setActiveTabKey(WORKFLOW_TAB_KEYS.ALL_WORKFLOWS);
    }
  }, [crltWorkflowEngine, dashboardFilterModel]);

  const showJiraGrid = !crltWorkflowEngine;

  const getColumnDefsCallback = useCallback(
    (filterOptions: Parameters<typeof getColumnDefs>[0]) =>
      getColumnDefs(filterOptions, context, showGpControlTower),
    [context, showGpControlTower],
  );

  const {
    columnDefs: ticketsColumnDefs,
    shouldShowGrid,
    saveViewLoading,
    handleSavedViewClicked,
    savedConfiguration,
    fieldMetadataMap,
    filterOptions,
    updateUrlFilters,
    updateUrlSort,
    clearUrlFilters,
  } = useGridViewManager({
    getColumnDefs: getColumnDefsCallback,
    gridApi,
    onSavedViewFilterApplied,
  });

  const dataSource = useMemo(() => {
    if (!fieldMetadataMap) return null;

    return new TicketsGraphqlDataSource(
      {
        onSortsChange,
        filterOptionsMap: filterOptions,
        fieldMetadataMap,
      },
      apolloClient,
    );
  }, [fieldMetadataMap, onSortsChange, filterOptions, apolloClient]);

  const prevExcludeAllCompletedRef = useRef<boolean | undefined>(undefined);
  const pendingRefreshRef = useRef<boolean>(false);

  useEffect(() => {
    if (!dataSource) return;

    const newValue = excludeAllCompleted ?? false;
    const prevValue = prevExcludeAllCompletedRef.current;

    dataSource.setExcludeAllCompleted(newValue);

    if (prevValue !== undefined && prevValue !== newValue) {
      pendingRefreshRef.current = true;
    }

    if (pendingRefreshRef.current && gridApi) {
      gridApi.refreshServerSide({ purge: true });
      pendingRefreshRef.current = false;
    }

    prevExcludeAllCompletedRef.current = newValue;
  }, [dataSource, excludeAllCompleted, gridApi]);

  const buildFilterModel = useCallback(
    (filters?: ExtendedTicketFilters): AgGrid.FilterModel => {
      const filterModel: AgGrid.FilterModel = {};

      if (filters?.statuses?.length) {
        filterModel.status = {
          filterType: "text",
          type: "equals",
          filter: filters.statuses[0],
        };
      }

      if (filters?.pastDueOnly !== undefined) {
        filterModel.pastDue = {
          filterType: "boolean",
          value: filters.pastDueOnly,
        };
      }

      if (filters?.workTypes?.length) {
        filterModel.workType = {
          filterType: "set",
          values: filters.workTypes,
        };
      }

      return filterModel;
    },
    [],
  );

  const handleGridReady = useCallback((event: AgGrid.GridReadyEvent) => {
    setGridApi(event.api);
  }, []);

  const handleFilterChanged = useCallback(
    (event: AgGrid.FilterChangedEvent) => {
      if (onFilterChanged) {
        onFilterChanged(event);
      }

      const filterModel = event.api.getFilterModel();
      updateUrlFilters(filterModel);
      event.api.paginationGoToFirstPage();
      event.api.refreshServerSide({ purge: true });
    },
    [onFilterChanged, updateUrlFilters],
  );

  const handleSortChanged = useCallback(
    (event: AgGrid.SortChangedEvent) => {
      const sortedColumns = event.api
        .getColumnState()
        .filter((col): col is AgGrid.ColumnState & { sort: "asc" | "desc" } => col.sort != null)
        .map((col) => ({ colId: col.colId, sort: col.sort }));
      const firstSort = sortedColumns.length > 0 ? [sortedColumns[0]] : null;
      updateUrlSort(firstSort);
      event.api.paginationGoToFirstPage();
      event.api.refreshServerSide({ purge: true });
    },
    [updateUrlSort],
  );

  useEffect(() => {
    if (!gridApi || !dataSource) return;

    gridApi.setGridOption("serverSideDatasource", dataSource);

    if (initialFiltersRef.current) {
      const currentFilterModel = gridApi.getFilterModel();
      const dashboardFilterModel = buildFilterModel(initialFiltersRef.current);
      const mergedFilterModel = { ...currentFilterModel, ...dashboardFilterModel };
      gridApi.paginationGoToFirstPage();
      gridApi.setFilterModel(mergedFilterModel);
    }
  }, [gridApi, dataSource, buildFilterModel]);

  useEffect(() => {
    if (!gridApi) return;

    const currentFilterModel = gridApi.getFilterModel();

    if (initialFilters) {
      const dashboardFilterModel = buildFilterModel(initialFilters);
      const mergedFilterModel = { ...currentFilterModel, ...dashboardFilterModel };
      gridApi.paginationGoToFirstPage();
      gridApi.setFilterModel(mergedFilterModel);
    }

    initialFiltersRef.current = initialFilters;
  }, [gridApi, initialFilters, buildFilterModel]);

  useEffect(() => {
    if (!gridApi) return;

    const shouldClear = clearDashboardFilters && !prevClearDashboardFiltersRef.current;

    if (shouldClear) {
      const currentFilterModel = gridApi.getFilterModel();
      delete currentFilterModel.workType;
      delete currentFilterModel.status;
      delete currentFilterModel.pastDue;
      gridApi.paginationGoToFirstPage();
      gridApi.setFilterModel(currentFilterModel);
    }

    prevClearDashboardFiltersRef.current = clearDashboardFilters;
  }, [gridApi, clearDashboardFilters]);

  useEffect(() => {
    if (!gridApi) return;

    const updateFilterState = () => {
      setHasActiveFilters(gridApi.isAnyFilterPresent());
    };

    updateFilterState();

    gridApi.addEventListener("filterChanged", updateFilterState);

    return () => {
      gridApi.removeEventListener("filterChanged", updateFilterState);
    };
  }, [gridApi]);

  const handleClearFilters = useCallback(() => {
    if (!gridApi) return;

    gridApi.paginationGoToFirstPage();
    gridApi.setFilterModel(null);
    // Reset grid sort state (setFilterModel only clears filters, not sorts)
    gridApi.applyColumnState({ defaultState: { sort: null } });
    gridApi.refreshServerSide?.({ purge: true });
    clearUrlFilters();
    onClearFilters?.();
  }, [gridApi, onClearFilters, clearUrlFilters]);

  const frameworkComponents = useMemo(
    () => ({
      bulkActionsPanel: (props: AgGrid.IToolPanelParams) =>
        createElement(BulkActionsPanel, { ...props, onOpenInJira: openTicketsInJira }),
      savedViewPanel: (props: AgGrid.IToolPanelParams) =>
        createElement(SavedViewPanel, {
          ...props,
          saveViewLoading,
          handleSavedViewClicked,
          savedConfiguration,
        }),
      exportPanel: (props: AgGrid.IToolPanelParams) =>
        createElement(ExportPanel, { ...props, dataSource: dataSource ?? undefined }),
    }),
    [saveViewLoading, handleSavedViewClicked, savedConfiguration, dataSource],
  );

  const gridOptions = useMemo(() => {
    const existingSideBar = defaultGridOptions?.sideBar;
    const existingToolPanels =
      typeof existingSideBar === "object" &&
      existingSideBar !== null &&
      "toolPanels" in existingSideBar
        ? existingSideBar.toolPanels || []
        : [];

    return {
      ...defaultGridOptions,
      sideBar: {
        position: "right" as const,
        toolPanels: [
          ...existingToolPanels,
          {
            id: "export",
            labelDefault: "Export",
            labelKey: "export",
            iconKey: "",
            toolPanel: "exportPanel",
          },
          {
            id: "savedView",
            labelDefault: "Save View",
            labelKey: "savedView",
            iconKey: "",
            toolPanel: "savedViewPanel",
          },
          {
            id: "columns",
            labelDefault: "Columns",
            labelKey: "columns",
            iconKey: "columns",
            toolPanel: "agColumnsToolPanel",
            toolPanelParams: {
              suppressRowGroups: true,
              suppressValues: true,
              suppressPivots: true,
              suppressPivotMode: true,
              suppressColumnFilter: false,
              suppressColumnSelectAll: false,
              suppressColumnExpandAll: true,
            },
          },
        ].filter(
          (panel) =>
            isCanopy ||
            ((panel as AgGrid.ToolPanelDef).id !== "bulkActions" &&
              (panel as AgGrid.ToolPanelDef).id !== "savedView"),
        ),
      },
    };
  }, [isCanopy]);

  const tabs = getControlTowerTabs();

  return (
    <>
      <Flex className={container} flexDirection="column">
        <Flex className={tableContainer} flexDirection="column">
          {(isCanopy || (isGPX && crltWorkflowEngine)) && (
            <Flex className={tableHeader} justifyContent="space-between" alignItems="center">
              {crltWorkflowEngine ? (
                <WorkflowTabs
                  tabs={tabs}
                  activeTabKey={activeTabKey}
                  onTabChange={setActiveTabKey}
                  rightContent={
                    workflowHasActiveFilters ? (
                      <Button
                        variant="neutral"
                        icon="times"
                        onClick={() => {
                          setWorkflowClearTrigger((t) => t + 1);
                          onClearFilters?.();
                        }}
                      >
                        Clear filters
                      </Button>
                    ) : undefined
                  }
                />
              ) : (
                <Text className={tableHeaderText} variant="regular-xl">
                  Workflows
                </Text>
              )}
              <Flex alignItems="center" gap={SPACING.SMALL} flexShrink={0}>
                {isGPX && crltWorkflowEngine && (
                  <Button
                    variant="primary"
                    icon="plus"
                    onClick={() => {
                      window.location.href = "/request-a-service";
                    }}
                    data-testid="gpx-new-request"
                  >
                    New request
                  </Button>
                )}
                {showJiraGrid && hasActiveFilters && (
                  <Button variant="neutral" icon="times" onClick={handleClearFilters}>
                    Clear filters
                  </Button>
                )}
              </Flex>
            </Flex>
          )}
          {crltWorkflowEngine ? (
            <WorkflowEngine
              key={activeTabKey}
              filterMode={
                activeTabKey === WORKFLOW_TAB_KEYS.ASSIGNED_TO_ME
                  ? "assigned_to_me"
                  : "all_open"
              }
              onHasActiveFiltersChange={setWorkflowHasActiveFilters}
              clearFiltersTrigger={workflowClearTrigger}
              dashboardFilterModel={dashboardFilterModel as AgGrid.FilterModel | null}
            />
          ) : (
            <div className={gridWrapper}>
              {shouldShowGrid && ticketsColumnDefs && (
                <Grid
                  columnDefs={ticketsColumnDefs}
                  gridOptions={gridOptions}
                  onGridReady={handleGridReady}
                  onCellClicked={onCellClicked}
                  onFilterChanged={handleFilterChanged}
                  onSortChanged={handleSortChanged}
                  components={frameworkComponents}
                  suppressColumnVirtualisation={false}
                  suppressRowVirtualisation={false}
                />
              )}
            </div>
          )}
        </Flex>
      </Flex>
      <WorkflowDetailsDrawer workflow={selectedWorkflow} onClose={onClose} />
    </>
  );
};

export default TicketsTable;
